import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CATEGORIES } from '../data';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { ArrowRight, Grid } from 'lucide-react';

const CATEGORY_IMAGES: Record<string, string> = {
  'Cars & Vehicles': '/assets/cars-and-vehicles.webp',
  Property: '/assets/property.webp',
  Mobiles: '/assets/mobile.webp',
  Jobs: '/assets/jobs.webp',
  Bikes: '/assets/bikes.webp',
  Electronics: '/assets/electronics.webp',
  Fashion: '/assets/fashion.webp',
  Furniture: '/assets/pexels-quang-nguyen-vinh-222549-14023300.webp',
};

const CATEGORY_SLUGS: Record<string, string> = {
  'Cars & Vehicles': 'cars-vehicles',
  Property: 'property',
  Mobiles: 'mobile-phones',
  Jobs: 'jobs',
  Bikes: 'motorbikes-scooters',
  Electronics: 'electronics',
  Fashion: 'fashion',
  Furniture: 'furniture',
};


export default function CategoriesPage(): JSX.Element {
  const { mobileOpen, toggleMobile } = useMobileMenu();

  return (
    <>
      <Helmet>
        <title>All Categories - AdsWorld</title>
        <meta name="description" content="Browse all categories including cars, property, mobiles, jobs, bikes, electronics, fashion, and furniture." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <ArrowRight className="w-4 h-4" />
            <span>All Categories</span>
          </nav>

          {/* Page Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 text-primary-400 text-sm font-semibold mb-4">
              <Grid className="w-5 h-5" />
              <span>Browse Categories</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              All Categories
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Browse listings across different categories. Find cars, property, mobiles, jobs, and more.
            </p>
          </div>

          {/* Categories Grid */}
          <div className="categories-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((category, index) => {
              const CardInner = (
                <div
                  className="group bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden fade-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                {/* Category Image */}
                <div className="h-40 relative overflow-hidden bg-dark-100">
                  <img
                    src={CATEGORY_IMAGES[category.name]}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/50 to-transparent" />
                  <p className="absolute bottom-4 left-4 text-xl font-bold text-white">
                    {category.name}
                  </p>
                </div>

                <div className="p-4">
                  {/* Category Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{category.count} listings</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
                </div>
              );

              return  (
                <Link
                  key={category.name}
                  to={`/category/${CATEGORY_SLUGS[category.name]}`}
                  aria-label={`Browse ${category.name}`}
                  className="block rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                >
                  {CardInner}
                </Link>
              );
            })}
          </div>
        </main>

        <Footer onOpenPrivacySettings={() => {}}/>
      </div>
    </>
  );
}
